"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Suite = {
  number: string;
  numeral: string;
  name: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  detailImage: string;
  detailSrcset: string;
  detailSizes: string;
  detailAlt: string;
  pieces: string;
  piece1: string;
  piece2: string;
  piece3: string;
  narrative: string;
};
const suites: Suite[] = [{
  number: "01",
  numeral: "§ 01",
  name: "Customer Support AI",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_support_automation.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_support_automation.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_support_automation.webp-wvc-sizes",
  alt: "AI-driven customer support interface with real-time analytics",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_support_node.webp",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_support_node.webp-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_support_node.webp-wvc-sizes",
  detailAlt: "Neural network node visualization",
  pieces: "Core operational infrastructure",
  piece1: "24/7 Intelligent Response Systems",
  piece2: "Sentiment Analysis Integration",
  piece3: "Multi-channel Ticket Routing",
  narrative: "We deploy autonomous support agents that resolve inquiries instantly. By leveraging natural language processing, your business maintains high-touch service standards without the overhead of manual intervention."
}, {
  number: "02",
  numeral: "§ 02",
  name: "Appointment Scheduling",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_booking_system.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_booking_system.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_booking_system.png-wvc-sizes",
  alt: "Automated calendar booking interface",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_calendar_sync.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_calendar_sync.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_calendar_sync.png-wvc-sizes",
  detailAlt: "Calendar synchronization icon",
  pieces: "Seamless scheduling architecture",
  piece1: "Dynamic Availability Sync",
  piece2: "Automated Confirmation Flows",
  piece3: "Smart Rescheduling Logic",
  narrative: "Eliminate the back-and-forth of manual scheduling. Our AI-powered booking systems integrate directly with your existing calendar, ensuring your time is optimized for high-value tasks."
}, {
  number: "03",
  numeral: "§ 03",
  name: "Invoicing Automation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_invoicing_flow.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_invoicing_flow.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_invoicing_flow.png-wvc-sizes",
  alt: "Automated financial invoicing dashboard",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_finance_ledger.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_finance_ledger.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/detail_finance_ledger.png-wvc-sizes",
  detailAlt: "Digital ledger visualization",
  pieces: "Financial precision tools",
  piece1: "Automated Invoice Generation",
  piece2: "Payment Gateway Integration",
  piece3: "Real-time Reconciliation",
  narrative: "Transform your billing cycle into a hands-off process. Our automation triggers invoices upon project completion, tracks payments, and sends reminders, ensuring your cash flow remains consistent."
}];
function SuiteBlock({
  suite,
  index
}: {
  suite: Suite;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isOdd = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`relative lg:col-span-7 ${isOdd ? "lg:order-1" : "lg:order-2"}`}>
        <div className="relative overflow-hidden">
          <img src={suite.image} data-wvc-srcset={suite.srcset} data-wvc-sizes={suite.sizes} alt={suite.alt} loading="lazy" decoding="async" className="w-full h-[480px] md:h-[600px] lg:h-[680px] object-cover" />
          <div className="absolute inset-0 bg-black/20 mix-blend-multiply pointer-events-none" />
        </div>

        <div className={`hidden md:block absolute ${isOdd ? "-right-6 lg:-right-10 bottom-16" : "-left-6 lg:-left-10 bottom-16"} w-36 h-36 lg:w-44 lg:h-44 overflow-hidden rounded-none border border-white/10`}>
          <img src={suite.detailImage} data-wvc-srcset={suite.detailSrcset} data-wvc-sizes={suite.detailSizes} alt={suite.detailAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className={`lg:col-span-5 flex flex-col justify-center ${isOdd ? "lg:order-2" : "lg:order-1"}`}>
        <div className="font-default text-[64px] md:text-[80px] leading-none text-white/0 mb-6" style={{
        WebkitTextStroke: "1px var(--foreground)"
      }}>
          {suite.numeral}
        </div>

        <h3 className="font-default text-3xl md:text-4xl lg:text-[44px] leading-tight text-white mb-8">
          {suite.name}
        </h3>

        <div className="font-mono text-xs tracking-[0.25em] uppercase text-foreground/70 mb-3">
          Technical Scope
        </div>
        <p className="font-default text-base text-white/70 italic mb-5">
          {suite.pieces}
        </p>

        <ul className="space-y-2.5 mb-8">
          <li className="font-default text-base text-white flex gap-3">
            <span className="text-foreground/70 mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 bg-foreground/70" />
            </span>
            <span>{suite.piece1}</span>
          </li>
          <li className="font-default text-base text-white flex gap-3">
            <span className="text-foreground/70 mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 bg-foreground/70" />
            </span>
            <span>{suite.piece2}</span>
          </li>
          <li className="font-default text-base text-white flex gap-3">
            <span className="text-foreground/70 mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 bg-foreground/70" />
            </span>
            <span>{suite.piece3}</span>
          </li>
        </ul>

        <p className="font-default text-[17px] leading-relaxed text-white/60 mb-10 max-w-xl">
          {suite.narrative}
        </p>

        <div className="flex flex-wrap items-center gap-6">
          <Link to="/AutomationEcosystemPage" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-white">
            <span className="relative">
              View technical specs
              <span className="absolute left-0 -bottom-1 h-px w-full bg-white origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500 ease-out" />
              <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground/70 origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out delay-100" />
            </span>
            <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <Button asChild size="lg" className="px-7 h-11 font-mono text-xs tracking-[0.2em] uppercase bg-foreground/70 text-background hover:bg-foreground/60">
            <Link to="/ConnectPage">Get started</Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function Expertise() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="5009" id="our-expertise" className="bg-background text-foreground pt-[100px] pb-32 md:pb-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-12">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-foreground/70 mb-8">
              Core Competencies — AI Integration
            </div>
            <h1 className="font-default text-5xl sm:text-6xl md:text-7xl lg:text-[96px] xl:text-[120px] leading-[0.95] tracking-tight text-white mb-10">
              Architecting
              <br />
              <span className="text-foreground/70">autonomous operations.</span>
            </h1>
            <p className="font-default text-lg md:text-[19px] leading-relaxed text-white/60 max-w-[540px]">
              We build intelligent workflows that eliminate operational friction. From automated customer support to financial reconciliation, our solutions are designed to scale your business efficiency through precision AI engineering.
            </p>
          </div>
        </div>

        <div className="space-y-32 md:space-y-44 lg:space-y-[180px]">
          {suites.map((suite, i) => <div key={suite.number} data-index={i}>
              <SuiteBlock suite={suite} index={i} />
              {i < suites.length - 1 && <div className="mt-32 md:mt-44 lg:mt-[180px] -mb-32 md:-mb-44 lg:-mb-[180px]">
                  <Separator className="bg-white/10" />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}